import { useLayoutEffect } from 'react'
import semanticCssUrl from 'fomantic-ui-less/semantic.less?url'

const [legacyGlobalCssUrl] = Object.values(
  import.meta.glob('../styles/global.scss', {
    query: '?url',
    import: 'default',
    eager: true,
  }),
)

const LEGACY_STYLESHEETS = [
  { id: 'legacy-styles', href: semanticCssUrl },
  { id: 'legacy-global-styles', href: legacyGlobalCssUrl },
]

function getLegacyStyleElements() {
  return LEGACY_STYLESHEETS.map(({ id }) => document.getElementById(id)).filter(
    Boolean,
  )
}

function whenSettled(link) {
  return new Promise((resolve) => {
    link.addEventListener('load', resolve, { once: true })
    link.addEventListener('error', resolve, { once: true })
  })
}

/**
 * Links Fomantic and the app's legacy global rules as two stylesheets, first
 * in `<head>` so they keep their place in the cascade ahead of component and
 * styled-components styles.
 *
 * Inserted at runtime, not imported as CSS, so migrated layouts can switch
 * them off: a stylesheet Vite injects from an import can never be removed
 * again. A `<link>` written into index.html would be merged into the entry CSS
 * by Vite and lose its id.
 *
 * @returns {Promise<void>} Settles once every stylesheet has loaded or failed,
 *   so the first render can wait for it and never paint unstyled.
 */
export function installLegacyStyles() {
  if (getLegacyStyleElements().length) return Promise.resolve()

  const links = LEGACY_STYLESHEETS.map(({ id, href }) => {
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href = href
    return link
  })
  const settled = Promise.all(links.map(whenSettled)).then(() => undefined)
  document.head.prepend(...links)
  return settled
}

/**
 * Disables the legacy stylesheets while the calling layout is mounted.
 * Layout effect, so the first paint of a migrated route never has Semantic
 * rules applied and the first paint after leaving it always does.
 */
export function useLegacyStylesDisabled() {
  useLayoutEffect(() => {
    const links = getLegacyStyleElements()
    links.forEach((link) => {
      link.disabled = true
    })
    return () => {
      links.forEach((link) => {
        link.disabled = false
      })
    }
  }, [])
}
